import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert
} from '@mui/material';
import Filter1Icon from '@mui/icons-material/Filter1'; // Assuming this is the icon for binary/decimal

const BinaryToDecimalPage: React.FC = () => {
  const [binaryInput, setBinaryInput] = useState<string>('');
  const [decimalOutput, setDecimalOutput] = useState<string>('');
  const [isValidBinary, setIsValidBinary] = useState<boolean>(true);

  useEffect(() => {
    if (binaryInput === '' || /^[01]+$/.test(binaryInput)) {
      setIsValidBinary(true);
    } else {
      setIsValidBinary(false);
    }
  }, [binaryInput]);

  const handleConvert = () => {
    if (!isValidBinary || !binaryInput) {
      setDecimalOutput('');
      return;
    }
    try {
      const decimalValue = parseInt(binaryInput, 2);
      setDecimalOutput(decimalValue.toString());
    } catch (error) {
      setDecimalOutput('Error during conversion');
      setIsValidBinary(false); // Should not happen with regex but as a fallback
    }
  };

  const handleClear = () => {
    setBinaryInput('');
    setDecimalOutput('');
    setIsValidBinary(true);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Filter1Icon sx={{ mr: 1.5, fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Binary to Decimal Converter
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter a binary number (e.g., 1011) to convert it to its decimal equivalent.
          </Typography>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enter Binary Number"
                variant="outlined"
                value={binaryInput}
                onChange={(e) => setBinaryInput(e.target.value)}
                error={!isValidBinary}
                helperText={!isValidBinary ? 'Invalid binary format (only 0s and 1s allowed)' : ''}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleConvert}
              disabled={!isValidBinary || !binaryInput}
            >
              Convert to Decimal
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
            >
              Clear
            </Button>
          </Box>

          {decimalOutput && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="h6">Decimal Result:</Typography>
              <Typography sx={{fontSize: '1.2rem', fontWeight: 'bold'}}>{decimalOutput}</Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BinaryToDecimalPage;
